import React, { useEffect, useState } from 'react';
import { Card, Form, Select, Button, Row, Col } from 'antd';
import { toast } from 'react-toastify';
import leadProspectApi from '../../api/leadProspectApi';
import { GetColumnSearchAndSortProps } from "../../components/Utils/GetColumnSearchAndSortProps";
import ColumnManagerTable from "../../components/Utils/ColumnManagerTable";

const { Option } = Select;

const FILTER_FIELDS = [
    { key: 'companyCountry', label: 'Countries' },
    { key: 'industry', label: 'Industry' },
    { key: 'departments', label: 'Departments' },
    { key: 'jobFunction', label: 'Job Function' },
    { key: 'jobLevel', label: 'Job Seniority' },
    { key: 'companyEmployeeSize', label: 'Company Size' },
    { key: 'jobTitle', label: 'Job Title' },
    { key: 'companyName', label: 'Company Name' },
    { key: 'companyDomain', label: 'Domains' },
    { key: 'source', label: 'Source' },
];

const CompanyEnrichPage = () => {

    const [form] = Form.useForm();

    const [filtersData, setFiltersData] = useState({});
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0
    });

    // =========================
    // LOAD FILTERS
    // =========================
    const fetchAllFilters = async () => {

        const fieldsPayload = FILTER_FIELDS.map(field => {

            const item = {
                fieldName: field.key
            };

            if (
                field.key === "source" ||
                field.key === "jobTitle" ||
                field.key === "companyName" ||
                field.key === "companyDomain"
            ) {
                item.topK = 100;
            }

            return item;
        });

        const res = await leadProspectApi.getDistinctValues(fieldsPayload);

        if (!res.success) {
            toast.error("Failed to load filters");
            return;
        }

        const data = res.data.data;

        const temp = {};

        FILTER_FIELDS.forEach(field => {
            temp[field.key] = data[field.key] || [];
        });

        setFiltersData(temp);
    };

    // =========================
    // FETCH LEADS
    // =========================
    const fetchLeads = async (payload = {}) => {

        setLoading(true);

        const res = await leadProspectApi.searchLeads(payload);

        if (res.success) {

            setResults(res.data.data.content);

            setPagination({
                current: res.data.data.page,
                pageSize: res.data.data.size,
                total: res.data.data.total
            });

        } else {
            toast.error(res.message || 'Failed to load leads');
        }

        setLoading(false);
    };

    // =========================
    // INITIAL LOAD
    // =========================
    useEffect(() => {
        fetchAllFilters();
        fetchDefaultLeads();
    }, []);

    // =========================
    // DEFAULT LEADS
    // =========================
    const fetchDefaultLeads = async () => {

        setLoading(true);

        const res = await leadProspectApi.searchLeads({
            page: 1,
            size: 10
        });

        if (res.success) {

            setResults(res.data.data.content);

            setPagination({
                current: res.data.data.page,
                pageSize: res.data.data.size,
                total: res.data.data.total
            });

        } else {
            toast.error(res.message || 'Failed to load leads');
        }

        setLoading(false);
    };

    // =========================
    // BUILD PAYLOAD
    // =========================
    const buildPayload = (values) => {

        const icpFilters = {};
        let domains = [];
        let topK;

        Object.keys(values).forEach((key) => {

            const value = values[key];

            if (!value || (Array.isArray(value) && value.length === 0)) {
                return;
            }

            if (key === 'companyDomain') {

                domains = value;

            } else if (key === 'topK') {

                topK = value;

            } else if (key === 'jobLevel') {

                icpFilters['jobSeniority'] = value;

            } else if (key === 'companyEmployeeSize') {

                icpFilters['companySize'] = value;

            } else {

                icpFilters[key] = value;
            }
        });

        return {
            ...(Object.keys(icpFilters).length > 0 && { icpFilters }),
            ...(domains.length > 0 && { domains }),
            ...(topK !== undefined && { topK }),
        };
    };

    // =========================
    // SEARCH
    // =========================
    const handleSearch = async () => {

        const values = form.getFieldsValue();

        const payload = buildPayload(values);

        payload.page = 1;
        payload.size = pagination.pageSize;

        await fetchLeads(payload);
    };

    // =========================
    // TABLE COLUMNS
    // =========================
    const columnsConfig = [
        {
            key: "companyName",
            title: "Name",
            checked: true,
            column: {
                title: "Name",
                dataIndex: "companyName",
                key: "companyName",
                ...GetColumnSearchAndSortProps("companyName", "Name", "string")
            }
        },
        {
            key: "companyPhone",
            title: "Phone",
            checked: true,
            column: {
                title: "Phone",
                dataIndex: "companyPhone",
                key: "companyPhone",
                ...GetColumnSearchAndSortProps("companyPhone", "Phone", "string")
            }
        },
        {
            key: "companyLinkedinUrl",
            title: "LinkedIn Url",
            checked: true,
            column: {
                title: "LinkedIn Url",
                dataIndex: "companyLinkedinUrl",
                key: "companyLinkedinUrl"
            }
        },
        {
            key: "companyEmployeeSize",
            title: "Employee Size",
            checked: true,
            column: {
                title: "Employee Size",
                dataIndex: "companyEmployeeSize",
                key: "companyEmployeeSize"
            }
        },
        {
            key: "companyDomain",
            title: "Domain",
            checked: true,
            column: {
                title: "Domain",
                dataIndex: "companyDomain",
                key: "companyDomain"
            }
        },
        {
            key: "industry",
            title: "Industry",
            checked: true,
            column: {
                title: "Industry",
                dataIndex: "industry",
                key: "industry"
            }
        },
        {
            key: "contacts",
            title: "Contacts",
            checked: true,
            column: {
                title: "Contacts",
                dataIndex: "contacts",
                key: "contacts"
            }
        },
        {
            key: "companyAddress",
            title: "Location",
            checked: true,
            column: {
                title: "Location",
                dataIndex: "companyAddress",
                key: "companyAddress",
                ...GetColumnSearchAndSortProps("companyAddress", "Location", "string")
            }
        }
    ];

    return (
        <div style={{ padding: 20 }}>

            {/* =========================
                FILTER CARD
            ========================= */}
            <Card
                title="Company Search (Dynamic Filters)"
                style={{ marginBottom: 20 }}
            >

                <Form form={form} layout="vertical">

                    <Row gutter={16}>

                        {FILTER_FIELDS.map(field => (

                            <Col span={8} key={field.key}>

                                <Form.Item
                                    name={field.key}
                                    label={field.label}
                                >

                                    <Select
                                        mode="multiple"
                                        placeholder={`Select ${field.label}`}
                                        allowClear
                                        showSearch
                                        optionFilterProp="children"
                                    >

                                        {filtersData[field.key]?.map(val => (
                                            <Option key={val} value={val}>
                                                {val}
                                            </Option>
                                        ))}

                                    </Select>

                                </Form.Item>

                            </Col>
                        ))}

                    </Row>

                    <Button
                        type="primary"
                        onClick={handleSearch}
                        loading={loading}
                    >
                        Search Leads
                    </Button>

                </Form>

            </Card>

            {/* =========================
                RESULTS CARD
            ========================= */}
            <Card
                title={
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}
                    >
                        <span>Results</span>

                        <span
                            style={{
                                fontWeight: 600,
                                color: '#1677ff'
                            }}
                        >
                            Total Companies: {pagination.total}
                        </span>
                    </div>
                }
            >

                <ColumnManagerTable
                    pageKey="company_enrich"
                    columnsConfig={columnsConfig}
                    data={results}
                    loading={loading}
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        showSizeChanger: true
                    }}
                    onChange={(paginationInfo) => {

                        const values = form.getFieldsValue();

                        const payload = buildPayload(values);

                        payload.page = paginationInfo.current;
                        payload.size = paginationInfo.pageSize;

                        fetchLeads(payload);
                    }}
                />

            </Card>

        </div>
    );
};

export default CompanyEnrichPage;